/* eslint-disable indent */
import styled, { css, keyframes } from "styled-components";

import { device } from "utils/sizes";
import { colourScheme, darken } from "utils/styling/colours";
import { Span, Text } from "utils/styling/typography.styled";

export const CardContainer = styled.div`
    width: 100%;
    max-width: 32rem;
    background-color: ${colourScheme.primaryLight};
    box-shadow:
        0 4px 6px ${colourScheme.shadow},
        0 1px 3px ${colourScheme.shadow};
    border-radius: 0.5rem;
    overflow: hidden;
    padding: 0 1rem;

    transition: background-color 0.2s;
    &:hover {
        background-color: ${darken(colourScheme.primary, 0.5)};
    }

    ${device.md} {
        width: 18rem;
    }
`;

export const Content = styled.div`
    padding: 0.5rem;

    ${device.md} {
        padding: 1rem;
    }
`;

export const Header = styled.div`
    display: flex;
    align-items: center;
    margin-bottom: 1rem; /* Equivalent to mb-4 */
`;

const subtlePulse = keyframes`
  0% { box-shadow: 0 0 0 0 rgba(255, 255, 255, 0.1); }
  50% { box-shadow: 0 0 0 4px rgba(255, 255, 255, 0.2); }
  100% { box-shadow: 0 0 0 0 rgba(255, 255, 255, 0.1); }
`;

const gradientShift = keyframes`
    0% { background-position: 0% 50%; }
    50% { background-position: 100% 50%; }
    100% { background-position: 0% 50%; }
`;

export const AvatarWrapper = styled.div<{ subscriptionstyle: any }>`
    position: relative;
    width: 5rem;
    height: 5rem;
    margin-right: 1rem; /* Equivalent to mr-4 */
    flex-shrink: 0;
    display: flex;
    align-items: center;
    justify-content: center;
    border-radius: 50%;
    overflow: hidden;

    ${(props) =>
        props.subscriptionstyle.gradientBorder &&
        css`
            background: ${props.subscriptionstyle.gradientBorder};
            background-origin: border-box;
            background-clip: content-box, border-box;
            ${props.subscriptionstyle.animation ===
                "subtlePulse 3s infinite, gradientShift 5s linear infinite" &&
            css`
                animation:
                    ${subtlePulse} 3s infinite,
                    ${gradientShift} 5s linear infinite;
            `}
            ${props.subscriptionstyle.animation === "subtlePulse" &&
            css`
                animation: ${subtlePulse} 3s infinite;
            `}
        `}

    ${(props) =>
        props.subscriptionstyle.borderColor &&
        css`
            border: 3px solid ${props.subscriptionstyle.borderColor};
            overflow: hidden;
            ${props.subscriptionstyle.animation === "subtlePulse" &&
            css`
                animation: ${subtlePulse} 3s infinite;
            `}
        `}
`;

export const AvatarImage = styled.img`
    width: 4.6rem;
    height: 4.6rem;
    border-radius: 50%;
    object-fit: cover;
`;

export const DefaultAvatar = styled.div`
    width: 100%;
    height: 100%;
    background-color: ${colourScheme.primaryLight};
    display: flex;
    align-items: center;
    justify-content: center;
`;

export const UserInfo = styled.div`
    display: flex;
    flex-direction: column;
`;

export const Username = styled(Span)`
    font-size: 1.125rem; /* Equivalent to text-lg */
    font-weight: 600; /* Equivalent to font-semibold */
    color: ${colourScheme.fullWhite};
    margin: 0;
`;

export const UserHandle = styled(Text)`
    font-size: 0.875rem; /* Equivalent to text-sm */
    color: ${colourScheme.greyLight};
    margin: 0;
`;

export const FollowButton = styled.button<{ isFollowing: boolean }>`
    width: 100%; /* Equivalent to w-full */
    padding: 0.5rem 0; /* Equivalent to py-2 */
    border-radius: 0.375rem; /* Equivalent to rounded-md */
    display: flex;
    align-items: center;
    justify-content: center;
    background-color: ${({ isFollowing }) =>
        isFollowing ? colourScheme.primary : colourScheme.secondary};
    color: ${colourScheme.fullWhite};
    transition: background-color 0.2s;
    cursor: pointer;
    border: none;
    font-size: 1rem;
    margin-top: 0.5rem;

    &:hover {
        background-color: ${({ isFollowing }) =>
            isFollowing
                ? colourScheme.primaryDark
                : colourScheme.secondaryDark};
    }

    svg {
        margin-right: 0.5rem; /* Equivalent to mr-2 */
    }
`;

export const StatsContainer = styled.div`
    display: flex;
    justify-content: space-between;
`;

export const StatItem = styled.div`
    display: flex;
    flex-direction: column;
    align-items: center;
`;

export const StatText = styled.div`
    display: flex;
    flex-direction: column;
    align-items: center;

    ${device.md} {
        flex-direction: row;
    }
`;

export const StatIcon = styled.div`
    display: flex;
    align-items: center;
    margin-bottom: 0.25rem; /* Equivalent to mb-1 */
    color: ${colourScheme.secondaryLight};
    ${device.md} {
        margin-bottom: 0;
        margin-right: 0.5rem;
    }
`;

export const StatValue = styled.span`
    font-size: 0.875rem; /* Equivalent to text-sm */
    font-weight: 600; /* Equivalent to font-semibold */
    color: ${colourScheme.fullWhite};
`;

export const StatLabel = styled.span`
    font-size: 0.75rem; /* Equivalent to text-xs */
    color: ${colourScheme.greyLight};
`;
